<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import type { Donation } from '@DonationModule';
import type { FieldSchema } from '../../../types';
import { applyShelterCurrency, getShelterCurrency } from '@Helpers';

const props = defineProps<{
  disabled?: boolean;
  donations?: Donation[];
  error?: string;
  field?: FieldSchema;
}>();

const model = defineModel<Donation | ''>({ default: '' });

const selectedDonation = ref<string | null>(null);
const otherAmount = ref<number | null>(null);

const activeDonations = computed(() => (props.field?.edit?.options as Donation[]) || props.donations || []);
const donationsOptions = computed(() =>
  activeDonations.value.map(({ _id, amount, description }) => {
    return {
      label: applyShelterCurrency(amount),
      labelTwo: `- ${description}`,
      value: _id,
    };
  })
);
const _model = computed((): Donation | '' => {
  if (!selectedDonation.value && !otherAmount.value) return '';

  return selectedDonation.value
    ? activeDonations.value.find(({ _id }) => _id === selectedDonation.value)!
    : ({
        _id: null,
        description: 'Custom Amount',
        amount: otherAmount.value,
      } as Donation);
});

onMounted(() => initModel());

watch(model, (newVal) => onModelUpdate(newVal ?? ''));
watch(_model, () => (model.value = _model.value));
watch(selectedDonation, (newValue) => newValue && (otherAmount.value = null));

function initModel() {
  if (!model.value) return;

  model.value._id ? (selectedDonation.value = model.value._id) : (otherAmount.value = model.value.amount);
}

function onModelUpdate(newVal: '' | Donation) {
  if (newVal) return initModel();

  selectedDonation.value = null;
  otherAmount.value = null;
}
</script>

<template>
  <div>
    <AFieldCheckboxButton
      v-model="selectedDonation"
      centeredList
      :error
      :options="donationsOptions"
    />

    <InputGroup class="pt-2">
      <InputGroupAddon :class="['h-9 text-sm', { 'af-field-error': !!error }]">
        {{ getShelterCurrency() }}
      </InputGroupAddon>

      <InputNumber
        v-model="otherAmount"
        mode="decimal"
        :class="['h-9 w-full text-sm placeholder:text-sm', { 'af-field-error': !!error }]"
        :maxFractionDigits="2"
        :minFractionDigits="2"
        :placeholder="$t('common.otherAmount')"
        :pt:input:root="['text-sm placeholder:text-sm', { 'af-field-error': !!error }]"
        @focus="selectedDonation = null"
        @input="(event) => (otherAmount = Number(event.value))"
      />
    </InputGroup>
  </div>
</template>
